/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useRef } from 'react';
import { X, Upload, AlertCircle, CheckCircle, Loader, FileText, AlertTriangle } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { SkillShopService } from '../../services/skillShopService';
import { normalizeAcceptanceCriteria } from '@shared/types/user-stories';
import {
  importFromCsv,
  readFileAsText,
  type CsvImportResult,
  type CsvEpic,
  type CsvUserStory,
} from '../../utils/jiraCsvExport';

interface CsvUserStoryImportDialogProps {
  onClose: () => void;
  onImportComplete: () => void;
}

/**
 * Dialog for importing user stories and epics from a CSV file.
 * Supports the same CSV format used for JIRA export.
 */
export function CsvUserStoryImportDialog({ onClose, onImportComplete }: CsvUserStoryImportDialogProps) {
  const { activeProduct } = useApp();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [step, setStep] = useState<'upload' | 'preview' | 'importing' | 'complete'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<CsvImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    successful: number;
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [skillShopService] = useState(() => new SkillShopService());

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setError(null);
    
    // Validate file type
    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }
    
    try {
      const content = await readFileAsText(selectedFile);
      const result = importFromCsv(content);
      
      if (result.errors.length > 0 && result.epics.length === 0 && result.stories.length === 0) {
        setError(result.errors.join('\n'));
        return;
      }
      
      setParseResult(result);
      setStep('preview');
    } catch (err) {
      setError(`Failed to read file: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFile = e.dataTransfer.files[0];
    if (!droppedFile) return;
    
    setFile(droppedFile);
    setError(null);
    
    if (!droppedFile.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }
    
    try {
      const content = await readFileAsText(droppedFile);
      const result = importFromCsv(content);
      
      if (result.errors.length > 0 && result.epics.length === 0 && result.stories.length === 0) {
        setError(result.errors.join('\n'));
        return;
      }
      
      setParseResult(result);
      setStep('preview');
    } catch (err) {
      setError(`Failed to read file: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const performImport = async () => {
    if (!parseResult || !activeProduct) return;
    
    setImporting(true);
    setError(null);
    setStep('importing');
    
    try {
      // Convert parsed data to the format expected by the API
      const epicsToImport = parseResult.epics.map(epic => ({
        id: epic.id,
        title: epic.title,
        description: epic.description || '',
        status: epic.status || 'Not Started',
      }));
      
      const storiesToImport = parseResult.stories.map(story => ({
        id: story.id,
        title: story.title,
        role: story.role || 'user',
        goal: story.goal || story.title,
        benefit: story.benefit || '',
        acceptanceCriteria: normalizeAcceptanceCriteria(story.acceptanceCriteria),
        status: story.status || 'Not Started',
        epicId: story.epicId,
      }));
      
      // Call the service to import
      const result = await skillShopService.importUserStoriesFromCsv(
        epicsToImport,
        storiesToImport,
        {
          updateExisting,
          repoUrl: activeProduct.repositoryUrl ?? undefined,
          repoPath: activeProduct.repoPath ?? undefined,
        }
      );
      
      if (result.success) {
        setImportResult(result.data || {
          successful: epicsToImport.length + storiesToImport.length,
          created: epicsToImport.length + storiesToImport.length,
          updated: 0,
          skipped: 0,
          errors: [],
        });
        setStep('complete');
      } else {
        setError(result.error || 'Import failed');
        setStep('preview');
      }
    } catch (err) {
      setError(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setStep('preview');
    }
    
    setImporting(false);
  };

  const handleComplete = () => {
    onImportComplete();
    onClose();
  };

  const resetUpload = () => {
    setFile(null);
    setParseResult(null);
    setError(null);
    setStep('upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Import User Stories from CSV
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700 mt-1 whitespace-pre-line">{error}</p>
            </div>
          </div>
        )}

        {/* No Active Product Warning */}
        {!activeProduct?.repositoryUrl && step === 'upload' && (
          <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">No Active Product</p>
              <p className="text-sm text-amber-700 mt-1">
                Please select a product with a repository on the Products page before importing.
                User stories will be saved to the active product's repository.
              </p>
            </div>
          </div>
        )}

        {/* Upload Step */}
        {step === 'upload' && (
          <div className="p-6">
            {/* Format Info */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Expected CSV Format</h4>
              <p className="text-sm text-blue-800 mb-2">
                The CSV should match the JIRA export format with these columns:
              </p>
              <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
                <li><strong>Summary</strong> - Format: "ID: Title" (e.g., "ADM-PLT-001: Create Account")</li>
                <li><strong>Issue Type</strong> - "Epic" or "Story"</li>
                <li><strong>Description</strong> - User story format: "As a..., I want to..., So that..."</li>
                <li><strong>Status</strong> - Optional (e.g., "To Do", "In Progress", "Done")</li>
                <li><strong>Epic Name</strong> - For stories, the parent epic's Summary</li>
              </ul>
            </div>

            {/* Target repository */}
            {activeProduct?.repositoryUrl && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-sm text-green-800">
                  <p><span className="font-medium">Target Product:</span> {activeProduct.title}</p>
                  <p><span className="font-medium">Repository:</span> {activeProduct.repositoryUrl}</p>
                  {activeProduct.repoPath && (
                    <p><span className="font-medium">Path:</span> {activeProduct.repoPath}</p>
                  )}
                </div>
              </div>
            )}

            {/* Drop Zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
            >
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                Drop your CSV file here
              </p>
              <p className="text-sm text-gray-500 mb-4">
                or click to browse
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              {file && (
                <p className="text-sm text-blue-600 font-medium">
                  Selected: {file.name}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Preview Step */}
        {step === 'preview' && parseResult && (
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Import Preview
              </h3>
              <p className="text-sm text-gray-600">
                Found {parseResult.epics.length} epic{parseResult.epics.length !== 1 ? 's' : ''} and {parseResult.stories.length} user stor{parseResult.stories.length !== 1 ? 'ies' : 'y'} to import
              </p>
              {file && (
                <p className="text-xs text-gray-500 mt-1">File: {file.name}</p>
              )}
            </div>

            {/* Warnings */}
            {parseResult.warnings.length > 0 && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Warnings</p>
                    <ul className="text-sm text-amber-700 mt-1 space-y-1">
                      {parseResult.warnings.slice(0, 5).map((warning, i) => (
                        <li key={i}>• {warning}</li>
                      ))}
                      {parseResult.warnings.length > 5 && (
                        <li>• ...and {parseResult.warnings.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Epics Table */}
            {parseResult.epics.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Epics ({parseResult.epics.length})</h4>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parseResult.epics.map((epic) => (
                        <tr key={epic.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{epic.id}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{epic.title}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{epic.status || 'Not Started'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Stories Table */}
            {parseResult.stories.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">User Stories ({parseResult.stories.length})</h4>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Epic</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parseResult.stories.map((story) => (
                        <tr key={story.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{story.id}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{story.title}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{story.epicId || '—'}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{story.status || 'Not Started'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Options */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="updateExisting"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="updateExisting" className="text-sm text-gray-700">
                  Update existing stories if found (based on ID)
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={resetUpload}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={performImport}
                disabled={importing || !activeProduct?.repositoryUrl}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {importing && <Loader className="w-4 h-4 animate-spin" />}
                {importing ? 'Importing...' : `Import ${parseResult.epics.length + parseResult.stories.length} Items`}
              </button>
            </div>
          </div>
        )}

        {/* Importing Step */}
        {step === 'importing' && (
          <div className="p-6 text-center">
            <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900">Importing user stories...</p>
            <p className="text-sm text-gray-600 mt-2">This may take a few moments</p>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && importResult && (
          <div className="p-6">
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Import Complete!</h3>
              <p className="text-gray-600">
                Successfully imported {importResult.successful || 0} items
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{importResult.created || 0}</p>
                <p className="text-sm text-green-900">Created</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{importResult.updated || 0}</p>
                <p className="text-sm text-blue-900">Updated</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-600">{importResult.skipped || 0}</p>
                <p className="text-sm text-gray-900">Skipped</p>
              </div>
            </div>

            {importResult.errors?.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-2">Errors:</h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <ul className="text-sm text-red-700 space-y-1">
                    {importResult.errors.map((err: string, index: number) => (
                      <li key={index}>• {err}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <button
              onClick={handleComplete}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
